import EventBus from 'app/common/eventbus/EventBus';
import {
    CapacityModel,
    ConfigData,
    DatasetDto,
    DatasetReferenceDto,
    DatasetReferenceType,
    DatasetType,
    NmSystemInstance,
    NmVersionId,
    TimePeriod,
} from 'app/model/CORE';
import { MonitoringPeriodSettings, TimeOptionEnum } from 'app/model/client/SOL49';
import { DashboardSettings } from 'app/components/panels/settings/SettingsPanel';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { ConvectionSource } from 'app/model/SOL49';
import * as less from 'less';

import Utils from '../util/Utils';
import TimeUtils from '../util/TimeUtils';

const MONITORING_PERIOD_SETTINGS_LS_KEY = 'monitoringPeriodSettings';
const DEFAULT_MONITORING_PERIOD_SETTINGS: MonitoringPeriodSettings = {
    deltaDay: 0,
    mode: TimeOptionEnum.RELATIVE_TO_NOW,
    beforeCurrentMn: 120,
    afterCurrentMn: 240,
    fromMs: 0,
    toMs: TimeUtils.DAY_TO_MS,
};

const DATASET_TYPE_LS_KEY = 'datasetType';
const DEFAULT_DATASET: DatasetDto = { type: DatasetType.OPERATIONAL };

const DASHBOARD_SETTINGS_LS_KEY = 'dashboardSettings';
const DEFAULT_DASHBOARD_SETTINGS = {
    capacityModel: CapacityModel.CCM,
    enableSoundNotification: true,
    convectionSource: ConvectionSource.DWD,
};

function loadMonitoringPeriodSettingsFromLocalStorage(): MonitoringPeriodSettings {
    if (!localStorage.getItem(MONITORING_PERIOD_SETTINGS_LS_KEY)) return;
    const settings: MonitoringPeriodSettings = JSON.parse(localStorage.getItem(MONITORING_PERIOD_SETTINGS_LS_KEY));
    if (!settings.deltaDay) {
        // Backward compatibility
        settings.deltaDay = 0;
    }

    if (!settings.mode) {
        settings.mode = TimeOptionEnum.RELATIVE_TO_NOW;
    }
    // Make sure period is valid
    const timePeriod = {
        from: TimeUtils.addMinutes(TimeUtils.NOW(), -settings.beforeCurrentMn),
        to: TimeUtils.addMinutes(TimeUtils.NOW(), settings.afterCurrentMn),
    };
    if (!TimePeriodUtils.isValid(timePeriod)) {
        settings.beforeCurrentMn = 120;
        settings.afterCurrentMn = 240;
    }
    if (!settings.fromMs || !settings.toMs || settings.fromMs >= settings.toMs) {
        settings.fromMs = 0;
        settings.toMs = TimeUtils.DAY_TO_MS;
    }

    return settings;
}

function loadDashboardSettingsFromLocalStorage(): DashboardSettings {
    if (!localStorage.getItem(DASHBOARD_SETTINGS_LS_KEY)) return;
    const settings: DashboardSettings = JSON.parse(localStorage.getItem(DASHBOARD_SETTINGS_LS_KEY));

    // In case browser had previous version settings in local storage without the convection source
    if (Utils.isNotExists(settings.convectionSource)) {
        settings.convectionSource = ConvectionSource.DWD;
        localStorage.setItem(DASHBOARD_SETTINGS_LS_KEY, JSON.stringify(settings));
    }

    return settings;
}

function loadDatasetFromLocalStorage(): DatasetDto {
    if (!localStorage.getItem(DATASET_TYPE_LS_KEY)) return;
    const datasetType: DatasetType = localStorage.getItem(DATASET_TYPE_LS_KEY) as DatasetType;

    if (!Utils.isExists(datasetType)) return;

    return { type: datasetType };
}

export default class AppContext {
    static DATASET_COLOR = { OPERATIONAL: 'blue', FORECAST: 'green' };
    static eventBus: EventBus = new EventBus();
    static configData: ConfigData;
    static monitoringPeriodSettings: MonitoringPeriodSettings =
        loadMonitoringPeriodSettingsFromLocalStorage() || DEFAULT_MONITORING_PERIOD_SETTINGS;
    private static monitoredPeriod: TimePeriod;
    static dashboardSettings: DashboardSettings = loadDashboardSettingsFromLocalStorage() || DEFAULT_DASHBOARD_SETTINGS;
    private static dataset: DatasetDto = loadDatasetFromLocalStorage() || DEFAULT_DATASET;

    static getCurrentTime() {
        return TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), TimeUtils.NOW());
    }

    static getMonitoredDay() {
        return TimeUtils.addDays(TimeUtils.withoutTimeOfDay(TimeUtils.NOW()), this.monitoringPeriodSettings.deltaDay);
    }

    static getTodayPeriod(): TimePeriod {
        const from = AppContext.getMonitoredDay();
        return {
            from,
            to: TimeUtils.addDays(from, 1),
        };
    }
    static mapIdCounter: number = 0;

    /**
     * return true if Application version >= refVersion
     * @param refVersion
     */
    static isVersionHigherOrEqualThan(refVersion: NmVersionId) {
        return AppContext.configData.nmVersionId >= refVersion; // behind the scenes, we rely on an eum string comparison where "R24" > "R23_5"
    }

    static isOpsBaseline(): boolean {
        return AppContext.configData.nmSystemInstance === NmSystemInstance.OPS;
    }

    static isNmvpBaseline(): boolean {
        return Utils.isInList(
            AppContext.configData.nmSystemInstance,
            NmSystemInstance.NMVP,
            NmSystemInstance.PRV,
            NmSystemInstance.NMSIM001,
            NmSystemInstance.NMSIM002,
            NmSystemInstance.NMSIM003,
        );
    }

    static isForecast = () => AppContext.getDataset().type === DatasetType.FORECAST;

    static getCapacityModel() {
        return AppContext.dashboardSettings.capacityModel;
    }

    static getConvectionSource() {
        return AppContext.dashboardSettings.convectionSource;
    }

    static getMonitoringPeriod() {
        if (
            new URLSearchParams(window.location.search).get('dataset') === 'static' ||
            new URLSearchParams(window.location.search).get('record-data') === '1'
        ) {
            const staticDate = new Date('2025-09-17T08:00:00.000+00:00');

            console.debug('Static dataset mode activated, using fixed date', staticDate);

            AppContext.monitoredPeriod = {
                from: TimeUtils.addMillis(staticDate, this.monitoringPeriodSettings.fromMs),
                to: TimeUtils.addMillis(staticDate, this.monitoringPeriodSettings.toMs),
            };

            TimeUtils.setSimulationTimeSync({ frozenTime: staticDate, timeShiftInMs: 0 });

            return AppContext.monitoredPeriod;
        }

        if (this.monitoringPeriodSettings.mode === TimeOptionEnum.RELATIVE_TO_NOW) {
            const currentTime = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), TimeUtils.NOW());
            AppContext.monitoredPeriod = {
                from: TimeUtils.addMinutes(currentTime, -this.monitoringPeriodSettings.beforeCurrentMn),
                to: TimeUtils.addMinutes(currentTime, this.monitoringPeriodSettings.afterCurrentMn),
            };
        } else {
            AppContext.monitoredPeriod = {
                from: TimeUtils.addMillis(AppContext.getMonitoredDay(), this.monitoringPeriodSettings.fromMs),
                to: TimeUtils.addMillis(AppContext.getMonitoredDay(), this.monitoringPeriodSettings.toMs),
            };
        }

        // Make sure the TimePeriod is inside the TimePeriodRange (in case it has been loaded from local storage)
        // Making sure to stay consistent with the GlobalTimeSelectionPopup logic (only shows range from 00:00 to 00:00)
        return TimePeriodUtils.intersection(AppContext.monitoredPeriod, AppContext.getTodayPeriod());
    }

    static getDataset = () => AppContext.dataset;

    static getDatasetReference(): DatasetReferenceDto {
        const dataset = AppContext.getDataset();

        return { type: DatasetReferenceType[dataset.type] };
    }

    static getZones = () => {
        return AppContext.configData && JSON.parse(AppContext.configData.properties['wholeNetworkColumnsJson']);
    };

    /* Set the monitoringPeriodSettings and store in the local storage */
    static setMonitoringPeriodSettings(monitoringPeriodSettings: MonitoringPeriodSettings) {
        AppContext.monitoringPeriodSettings = monitoringPeriodSettings;
        localStorage.setItem(MONITORING_PERIOD_SETTINGS_LS_KEY, JSON.stringify(monitoringPeriodSettings));

        if (this.monitoringPeriodSettings.mode === TimeOptionEnum.RELATIVE_TO_NOW) {
            const currentTime = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), TimeUtils.NOW());
            AppContext.monitoredPeriod = {
                from: TimeUtils.addMinutes(currentTime, -AppContext.monitoringPeriodSettings.beforeCurrentMn),
                to: TimeUtils.addMinutes(currentTime, AppContext.monitoringPeriodSettings.afterCurrentMn),
            };
        } else {
            AppContext.monitoredPeriod = {
                from: TimeUtils.addMillis(AppContext.getMonitoredDay(), this.monitoringPeriodSettings.fromMs),
                to: TimeUtils.addMillis(AppContext.getMonitoredDay(), this.monitoringPeriodSettings.toMs),
            };
        }
    }

    /* Set the dashboardSettings and store in the local storage */
    static setDashboardSettings(dashboardSettings: DashboardSettings) {
        AppContext.dashboardSettings = dashboardSettings;
        localStorage.setItem(DASHBOARD_SETTINGS_LS_KEY, JSON.stringify(dashboardSettings));
    }

    static isFmpCoordinationEnabled(): boolean {
        return AppContext.configData.properties['fmpCoordinationEnabled'] === 'true';
    }

    static setDataset(dataset: DatasetDto) {
        AppContext.dataset = dataset;
        localStorage.setItem(DATASET_TYPE_LS_KEY, dataset.type);
        // Swap tone colors
        less.modifyVars({
            '@base-color': AppContext.DATASET_COLOR[dataset.type],
        }).catch(err => console.error('Error updating less ! ', err));
    }

    static getNewMapId() {
        return this.mapIdCounter++;
    }
}
